import { DependencyList, useEffect, useRef } from 'react';

import { logInDev } from './log-in-dev.util';

type CleanupFn = () => void;
type AsyncEffect = (signal: AbortSignal) => void | CleanupFn | Promise<void | CleanupFn>;

const isAbortError = (error: unknown) =>
  (error instanceof DOMException || error instanceof Error) && error.name === 'AbortError';

export const useAsyncEffect = (effect: AsyncEffect, deps: DependencyList) => {
  const effectRef = useRef(effect);

  useEffect(() => {
    effectRef.current = effect;
  }, [effect]);

  useEffect(() => {
    const controller = new AbortController();
    let cleanup: CleanupFn | undefined;
    let shouldCleanupImmediately = false;

    const runCleanup = () => {
      if (!cleanup) {
        return;
      }

      const cleanupFn = cleanup;
      cleanup = undefined;
      cleanupFn();
    };

    Promise.resolve()
      .then(() => effectRef.current(controller.signal))
      .then((result) => {
        if (typeof result !== 'function') {
          return;
        }
        cleanup = result;
        // If the effect finishes after unmount, run its cleanup immediately.
        if (shouldCleanupImmediately) {
          runCleanup();
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        logInDev(error);
      });

    return () => {
      shouldCleanupImmediately = true;
      controller.abort();
      runCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
